import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UploadZone = ({ onFilesSelected, acceptedFormats, maxFileSize, isProcessing }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e?.dataTransfer?.files);
    if (files?.length > 0) {
      onFilesSelected(files);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e?.target?.files);
    if (files?.length > 0) {
      onFilesSelected(files);
    }
  };

  const handleBrowseClick = (e) => {
    e?.stopPropagation(); // Prevent event bubbling to parent div
    fileInputRef?.current?.click();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      <div
        className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 ${
          isDragOver
            ? 'border-primary bg-primary/5 scale-[1.02]'
            : 'border-border hover:border-primary/50 hover:bg-muted/30'
        } ${isProcessing ? 'opacity-50 pointer-events-none' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleBrowseClick}
        style={{ cursor: 'pointer' }}
      >
        {/* Upload Icon */}
        <div className="mb-4">
          <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
            <Icon 
              name={isDragOver ? "FileUp" : "Upload"} 
              size={24} 
              color="var(--color-primary)"
              className={isDragOver ? "animate-bounce" : ""}
            />
          </div>
        </div>

        {/* Main Text */}
        <div className="mb-4">
          <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
            {isDragOver ? 'Drop files here' : 'Drag and drop your files here or click to browse'}
          </h3>
          <p className="text-muted-foreground font-body text-sm mb-2">
            Analysis starts immediately after upload
          </p>
        </div>

        {/* File Format Info */}
        <div className="flex items-center justify-center space-x-6 text-xs text-muted-foreground mb-4">
          <div className="flex items-center space-x-1">
            <Icon name="FileText" size={12} />
            <span>PDF, DOC, DOCX</span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="Database" size={12} />
            <span>Max 5 MB</span>
          </div>
        </div>

        {/* Browse Button */}
        <div>
          <Button
            variant="default"
            size="md"
            onClick={handleBrowseClick}
            disabled={isProcessing}
            iconName="FolderOpen"
            iconPosition="left"
            iconSize={16}
          >
            Browse Files
          </Button>
        </div>

        {/* Processing Overlay */}
        {isProcessing && (
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm rounded-xl flex items-center justify-center">
            <div className="text-center">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
              <p className="text-sm text-muted-foreground">Processing files...</p>
            </div>
          </div>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.doc,.docx,application/pdf"
        onChange={handleFileSelect}
        className="hidden"
        style={{ display: 'none' }}
      />

      {/* Supported Formats */}
      <div className="flex items-center justify-center space-x-6 text-xs text-muted-foreground">
        <span className="font-medium">Supported Formats</span>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 bg-success rounded-full"></div>
            <span>Secure Upload</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 bg-primary rounded-full"></div>
            <span>Auto-Analyze</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 bg-accent rounded-full"></div>
            <span>Multi-State Support</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadZone;
